import { useEffect, useState } from "react";
import { colors } from "../../../assets/colors";

import { ProjectsTypes } from "../../../interfaces/ProjectsTypes";
import { Box, Typography } from "@mui/material";
import Input from "../../../components/Input";
import Row from "../../../components/layout/Row";
import { SuiteTypes } from "../../../interfaces/SuiteTypes";
import { updateProject } from "../../../api/projects";
import { toast } from "react-toastify";

interface Props {
  currentProject: ProjectsTypes;
  suites: SuiteTypes[];
}

export default function PyramidTab({ currentProject, suites }: Props) {
  const [testesUnitarios, setTestesUnitarios] = useState(0);
  const [testesIntegracao, setTestesIntegracao] = useState(0);
  const [testesManual, setTestesManual] = useState(0);
  const [testesAutomatizados, setTestesAutomatizados] = useState(0);
  const [mensagem, setMensagem] = useState("");
  const [status, setStatus] = useState("");

  const handleSubmit = (e: any) => {
    e.preventDefault();
  };

  useEffect(() => {
    const calculeteType = () => {
      const typeCounts = suites.reduce(
        (acc: { [key: string]: number }, suite) => {
          suite.test_cases.forEach((testCase) => {
            if (testCase.type) {
              acc[testCase.type] = (acc[testCase.type] || 0) + 1;
            }
          });
          return acc;
        },
        {}
      );

      setTestesManual(typeCounts.MANUAL || 0);
      setTestesAutomatizados(typeCounts.AUTOMATED || 0);
      setTestesUnitarios(currentProject.unitTests || 0);
      setTestesIntegracao(typeCounts.INTEGRATION || 0);
    };

    calculeteType();
  }, []);

  useEffect(() => {
    verificarPiramide();
  }, [testesUnitarios]);

  const verificarPiramide = () => {
    let totalUnitarios = testesUnitarios;
    let totalIntegracao = testesIntegracao;
    let totalAutomatizados = testesAutomatizados;
    let totalManuais = testesManual;
    let totalGeral =
      totalUnitarios + totalIntegracao + totalAutomatizados + totalManuais;

    // Evita divisão por zero
    if (totalGeral === 0) {
      setMensagem(
        "🚨 Ruim: A quantidade de testes de UI é maior, considere melhorar a distribuição dos testes."
      );
      setStatus("error");
      return;
    }

    if (
      totalUnitarios > totalIntegracao &&
      totalIntegracao > totalAutomatizados &&
      totalAutomatizados > totalManuais
    ) {
      setMensagem(
        "✅ Aprovado: A quantidade de testes unitários é maior que todos, e a de testes de UI é menor que todos."
      );
      setStatus("ok");
      return;
    } else if (
      totalUnitarios >= totalIntegracao &&
      totalIntegracao >= totalAutomatizados &&
      totalManuais <= totalAutomatizados
    ) {
      setMensagem(
        "⚠️ Pode melhorar: A quantidade de testes unitários é maior, mas a quantidade de testes de integração é a menor."
      );
      setStatus("warning");
      return;
    } else {
      setMensagem(
        "🚨 Ruim: A quantidade de testes de UI é maior, considere melhorar a distribuição dos testes."
      );
      setStatus("error");
      return;
    }
  };

  const handleSaveUnitTests = () => {
    updateProject(currentProject.project_id, {
      unitTests: testesUnitarios,
    }).then(() => {
      toast.success("Quantidade de testes atualizada com sucesso!");
    });
  };

  return (
    <Box sx={styles.content}>
      <Typography sx={styles.title}>
        Quantidade de testes baseado nos cenários criados.
      </Typography>

      <form onSubmit={handleSubmit}>
        <Row style={styles.formContent}>
          <Input
            title="Testes Manuais"
            type="number"
            value={String(testesManual)}
            onChange={(e) => setTestesManual(Number(e.target.value))}
            disabled
          />
          <Input
            title="Testes Automatizados"
            type="number"
            value={String(testesAutomatizados)}
            onChange={(e) => setTestesAutomatizados(Number(e.target.value))}
            disabled
          />
          <Input
            title="Testes de Integração"
            type="number"
            value={String(testesIntegracao)}
            onChange={(e) => setTestesIntegracao(Number(e.target.value))}
            disabled
          />
          <Input
            title="Testes Unitários"
            type="number"
            value={String(testesUnitarios)}
            onChange={(e) => setTestesUnitarios(Number(e.target.value))}
            endIcon={
              <i
                className="material-icons"
                style={styles.saveIcon}
                onClick={handleSaveUnitTests}
              >
                save
              </i>
            }
          />
        </Row>
      </form>

      {mensagem && (
        <Box
          sx={{
            ...styles.messageContainer,
            backgroundColor:
              status === "ok"
                ? "#e6ffe6"
                : status === "warning"
                ? "#fff2d8"
                : "#fee7e7",
          }}
        >
          <span
            style={{
              ...styles.message,
              color:
                status === "ok"
                  ? "green"
                  : status === "warning"
                  ? "orange"
                  : "red",
            }}
          >
            {mensagem}
          </span>
          <a
            href="https://medium.com/creditas-tech/a-pir%C3%A2mide-de-testes-a0faec465cc2"
            target="_blank"
            rel="noopener noreferrer"
          >
            Conhecer mais sobre a Piramide de Testes
          </a>
        </Box>
      )}

      <Box sx={styles.pyramidContainer}>
        <Box
          sx={{
            top: 0,
            width: `${testesManual}px`,
            ...styles.pyramidLine,
          }}
        >
          Manual
        </Box>
        <Box
          sx={{
            top: 100,
            width: `${testesAutomatizados}px`,
            ...styles.pyramidLine,
          }}
        >
          Automatizados
        </Box>
        <Box
          sx={{
            top: 200,
            width: `${testesIntegracao}px`,
            ...styles.pyramidLine,
          }}
        >
          Integração
        </Box>

        <Box
          sx={{
            top: 300,
            width: `${testesUnitarios}px`,
            ...styles.pyramidLine,
          }}
        >
          Unitários
        </Box>
      </Box>
    </Box>
  );
}
const styles: any = {
  loadingContent: {
    backgroundColor: colors.white,
    marginTop: 50,
    marginLeft: 12,
    marginRight: 12,
  },

  content: {
    padding: 4,
    textAlign: "center",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
  },

  title: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
  },

  formContent: {
    width: "100%",
    alignItems: "center",
    justifyContent: "center",
    gap: 16,
    textAlign: "left",
  },

  saveIcon: {
    color: colors.primary,
    fontSize: 24,
    cursor: "pointer",
  },

  messageContainer: {
    padding: 3,
    borderRadius: 4,
    marginTop: 4,
    display: "flex",
    flexDirection: "column",
    width: "80%",
    alignSelf: "center",
  },

  message: {
    fontSize: 18,
    fontWeight: 500,
  },

  pyramidLine: {
    position: "absolute",
    left: "50%",
    transform: "translateX(-50%)",
    height: "90px",
    backgroundColor: "lightblue",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 16,
  },

  pyramidContainer: {
    marginTop: 4,
    position: "relative",
    height: "200px",
  },
};
